import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDays, format, isAfter, isBefore, isSameDay, parseISO, startOfDay } from 'date-fns'
import {
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Phone,
  User,
  XCircle,
} from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { formatDateTimeRange, localizedName } from '@/lib/format'
import { useT, getT } from '@/lib/i18n'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import type { BookingOut } from '@/shared/types'
import { StorefrontPage, StorefrontCard, StorefrontEmptyState, StorefrontLoading } from '@/components/StorefrontPage'


const STATUS_ICON: Record<string, string> = {
  pending: '⏳',
  confirmed: '✅',
  cancelled: '❌',
  done: '✔️',
  no_show: '🚫',
}

type BookingFilter = 'all' | 'active' | 'past' | 'cancelled'

function formatSlot(starts: string, ends: string, lang: 'uk' | 'en' | 'ru') {
  return formatDateTimeRange(starts, ends, lang)
}

function BookingRow({ booking, lang }: { booking: BookingOut; lang: 'uk' | 'en' | 'ru' }) {
  const [open, setOpen] = useState(false)
  const T = getT(lang, 'bookings')
  const qc = useQueryClient()
  const statusLabel = T.status[booking.status] ?? booking.status
  const canCancel = booking.status === 'pending' || booking.status === 'confirmed'
  const name = localizedName(lang, booking.service_name_uk, booking.service_name_en)

  const cancelMutation = useMutation<unknown, ApiError>({
    mutationFn: () => api.delete(`/bookings/${booking.id}`),
    onSuccess: () => {
      haptic('success')
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
    onError: (err) => {
      haptic('error')
      alert(err.message)
    },
  })

  function handleCancel() {
    if (!window.confirm(T.confirmCancel(booking.id))) return
    haptic('medium')
    cancelMutation.mutate()
  }

  return (
    <StorefrontCard className="mb-3 overflow-hidden">
      <button
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50/50"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-lg">{STATUS_ICON[booking.status] ?? '📅'}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-sm text-slate-900">{name}</p>
          <p className="text-xs text-slate-500">{formatSlot(booking.starts_at, booking.ends_at, lang)}</p>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="space-y-2.5 bg-slate-50/60 px-4 pb-4 pt-1 border-t border-slate-100">
          <p className="text-xs">
            <span className="inline-flex items-center gap-1 font-semibold text-slate-800">
              <span>{STATUS_ICON[booking.status] ?? '·'}</span>
              <span>#{booking.id} — {statusLabel}</span>
            </span>
          </p>

          <p className="text-xs text-slate-700">
            <User className="mr-1 inline h-3.5 w-3.5 text-slate-400" />
            {T.provider}: {booking.provider_name}
          </p>

          {booking.customer_contact && (
            <p className="text-xs text-slate-700">
              <Phone className="mr-1 inline h-3.5 w-3.5 text-slate-400" />
              {T.contact}: {booking.customer_contact}
            </p>
          )}

          {booking.created_at && (
            <p className="text-xs text-slate-500">
              {T.created}: {formatDateTimeRange(booking.created_at, booking.created_at, lang)}
            </p>
          )}

          {booking.paid ? (
            <p className="font-semibold text-emerald-600 text-xs">✅ {T.paid}</p>
          ) : (
            <p className="text-xs font-semibold text-amber-600">💳 {T.unpaid}</p>
          )}

          {booking.calendar_conflict && booking.status === 'pending' && (
            <p className="text-xs text-amber-600 font-medium">{T.calendarPending}</p>
          )}

          {booking.customer_notes && (
            <div className="rounded-xl border border-slate-200/80 bg-white px-3 py-2 text-xs text-slate-600">
              <p className="mb-0.5 font-semibold text-slate-900">{T.notes}</p>
              <p>{booking.customer_notes}</p>
            </div>
          )}

          {booking.cancellation_reason && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 px-3 py-2 text-xs text-rose-700">
              <p className="mb-0.5 font-semibold">{T.cancellationReason}</p>
              <p>{booking.cancellation_reason}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-200/60">
            {booking.status !== 'cancelled' && (
              <a
                href={`/api/bookings/${booking.id}/ics`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs transition"
              >
                <CalendarPlus className="h-3.5 w-3.5" />
                {T.addToCalendar}
              </a>
            )}
            {canCancel && (
              <button
                disabled={cancelMutation.isPending}
                onClick={handleCancel}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white hover:bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 shadow-2xs transition"
              >
                <XCircle className="h-3.5 w-3.5" />
                {cancelMutation.isPending ? T.cancelling : T.cancel}
              </button>
            )}
          </div>
        </div>
      )}
    </StorefrontCard>
  )
}

export function BookingsPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('bookings')
  const common = useT('common')
  const [filter, setFilter] = useState<BookingFilter>('active')
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const { data: bookings, isLoading } = useQuery<BookingOut[]>({
    queryKey: ['bookings'],
    queryFn: () => api.get<BookingOut[]>('/bookings'),
  })

  if (isLoading) return <StorefrontLoading message={common.loading} />

  if (!bookings?.length) {
    return <StorefrontEmptyState icon={CalendarDays} title={T.empty} />
  }

  const today = startOfDay(new Date())
  const days = Array.from({ length: 7 }, (_, idx) => addDays(today, idx))
  const filteredBookings = bookings.filter((booking) => {
    const startsAt = parseISO(booking.starts_at)
    if (selectedDay && !isSameDay(startsAt, parseISO(selectedDay))) {
      return false
    }
    if (filter === 'cancelled') return booking.status === 'cancelled'
    if (filter === 'past') {
      return booking.status !== 'cancelled' && isBefore(startsAt, today)
    }
    if (filter === 'active') {
      return booking.status !== 'cancelled' && (isSameDay(startsAt, today) || isAfter(startsAt, today))
    }
    return true
  })

  return (
    <StorefrontPage
      title={T.title}
      badge={bookings.length}
    >
      <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-none">
        {(['active', 'all', 'past', 'cancelled'] as const).map((value) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition ${
              filter === value
                ? 'border-brand-600 bg-brand-600 text-white shadow-2xs'
                : 'border-slate-200/80 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {T.filters[value]}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-3.5 scrollbar-none">
        <button
          onClick={() => setSelectedDay(null)}
          className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
            selectedDay === null
              ? 'border-brand-600 bg-brand-600 text-white shadow-2xs'
              : 'border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          {T.anyDay}
        </button>
        {days.map((day) => {
          const iso = format(day, 'yyyy-MM-dd')
          const isSelected = selectedDay === iso
          return (
            <button
              key={iso}
              onClick={() => setSelectedDay(iso)}
              className={`shrink-0 rounded-xl border px-3 py-1.5 text-center text-xs font-semibold transition ${
                isSelected
                  ? 'border-brand-600 bg-brand-600 text-white shadow-2xs'
                  : 'border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="block text-[10px] opacity-75">{format(day, 'EEE')}</span>
              <span className="block font-bold">{format(day, 'dd.MM')}</span>
            </button>
          )
        })}
      </div>
      <div className="space-y-3">
        {filteredBookings.map((booking) => (
          <BookingRow key={booking.id} booking={booking} lang={lang} />
        ))}
      </div>
      {filteredBookings.length === 0 && (
        <div className="py-8 text-center text-xs text-slate-400 font-medium">{T.empty}</div>
      )}
      <div className="mt-4 rounded-xl bg-slate-100/70 border border-slate-200/60 p-3 text-[11px] text-slate-500">
        <CircleAlert className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />
        {lang === 'en'
          ? 'Booking details are kept in sync with the bot flow, including calendar import and cancellation handling.'
          : 'Деталі запису синхронізовані з ботом, включно з додаванням у календар та обробкою скасування.'}
      </div>
    </StorefrontPage>
  )
}
